<script lang="ts">
	import { getClosePositionPostTradeDetails } from '$lib/data/userInteract';
	import { limitOrders, markets } from '$lib/home/store';
	import { closingPosition } from '$lib/home/userInteract/store';
	import {
		closePosition,
		getTradeStatus,
		openAdjustMarginModal
	} from '$lib/home/userInteract/userInteract';
	import { getDisplayName } from '$lib/perps';
	import { invalidLimitOrderStatus, LimitStatus, TradeStatus } from '$lib/types/userInteract';
	import DisabledButton from '$lib/util/DisabledButton.svelte';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import ProcessingButton from '$lib/util/ProcessingButton.svelte';
	import SwitchToOptimismButton from '$lib/util/SwitchToOptimismButton.svelte';
	import {
		addDollarSign,
		debounce,
		getNumBackInUnits,
		getShortAddress,
		sanitizeInputs,
		toBN
	} from '$lib/util/util';
	import { chain, walletConnected, walletLoaded } from '$lib/wallet/store';
	import { MARKET_ADDRESES } from '$lib/web3/addresses';
	import BigNumber from 'bignumber.js';
	import {
		closePositionInput,
		closePositionOrderType,
		closePositionPostTradeDetails,
		currentClosePosition,
		loadingClosePositionPostTradeDetails,
		ClosePositionLimitStatus
	} from '../popupsStore';
	import ClosePositionLimit from './ClosePositionLimit.svelte';
	import ClosePositionMarket from './ClosePositionMarket.svelte';
	import EstimatedCloseOpRewards from './EstimatedCloseOpRewards.svelte';
	import { onMount } from 'svelte';

	onMount(() => {
		getClosePositionPostTradeDetails();
	});

	let inputVal: string = '';

	function setRatioAmount(ratio: number) {
		if (closePositionMarket) {
			$closePositionInput = new BigNumber($currentClosePosition.size)
				.multipliedBy(ratio)
				.abs()
				.toString();
		}
		inputVal = getNumBackInUnits($closePositionInput, 18, 6);
		getClosePositionPostTradeDetails();
	}

	function inputChanged() {
		inputVal = sanitizeInputs(inputVal);
		if (inputVal) $closePositionInput = toBN(inputVal).toString();
		else $closePositionInput = '0';
	}

	let isAmountValid: boolean = false;
	let isAmountGreaterThanAvailable: boolean = false;

	$: if ($currentClosePosition && $closePositionInput && closePositionMarket) {
		if (
			new BigNumber($closePositionInput).gt(0) &&
			new BigNumber($currentClosePosition.size).abs().gte(new BigNumber($closePositionInput))
		) {
			isAmountValid = true;
			isAmountGreaterThanAvailable = false;
		} else {
			isAmountValid = false;
			if (new BigNumber($closePositionInput).gt(0)) {
				isAmountGreaterThanAvailable = true;
			} else {
				isAmountGreaterThanAvailable = false;
			}
		}
	}

	$: closePositionMarket = $markets.find(
		(e) => e.marketIdentifier == $currentClosePosition.marketIdentifier
	);

	const KEEPER_DEPOSIT = '4.00';
	let expectedLimitFee: string = '0';
	let expectedstandardOrderFee: string = '0';
	$: expectedLimitFee = $closePositionInput
		? new BigNumber($closePositionInput).multipliedBy(3).div(10000).toString()
		: '0';

	$: expectedstandardOrderFee =
		$closePositionInput && $closePositionInput != '0' && $closePositionPostTradeDetails?.fee
			? new BigNumber($closePositionPostTradeDetails.fee)
					.plus(new BigNumber($closePositionPostTradeDetails.keeperFee))
					.toString()
			: '0';

	$: totalLimitFee =
		$closePositionInput != '0'
			? new BigNumber(expectedLimitFee)
					.plus(new BigNumber(expectedstandardOrderFee))
					.plus(new BigNumber(KEEPER_DEPOSIT).multipliedBy(1e18))
					.toString()
			: '0';

	let closePositionMargin: string = '0';
	$: closePositionMargin = $closePositionPostTradeDetails?.totalMargin
		? new BigNumber($closePositionPostTradeDetails.totalMargin).toString()
		: '0';

	let isPairOrder: boolean;
	$: isPairOrder =
		$currentClosePosition &&
		(new BigNumber($currentClosePosition.takeProfit).gt(0) ||
			new BigNumber($currentClosePosition.stopLoss).gt(0));

	function handleClosePosition() {
		closePosition();
	}
</script>

<GradientBorder
	gradient="sm:bg-fx-gradient-6"
	paddingClass="p-px"
	borderRadius="rounded-2xl squircle-2xl"
	outerClassList="w-full sm:w-fit h-fit"
	innerClassList="bg-fx-black"
>
	<div
		class="bg-fx-gradient-8-50 text-fx-white p-4 sm:p-8 rounded-2xl w-full sm:w-fit sm:min-w-[400px]"
	>
		<div class="text-fx-gradient-5 text-xl font-semibold">Close Position</div>
		<div class="border-t border-t-fx-grey-800 w-full mb-6 mt-2" />

		<div>
			<div class="flex justify-between items-end">
				<div class="text-xs sm:text-sm text-fx-grey-400">Market</div>
				<div class="text-xs sm:text-sm text-fx-grey-300">
					{getDisplayName($currentClosePosition.marketIdentifier)}
				</div>
			</div>
			<div class="flex justify-between items-end">
				<div class="text-xs sm:text-sm text-fx-grey-400">Current Price</div>
				<div class="text-xs sm:text-sm text-fx-grey-200 font-semibold">
					{#if closePositionMarket}
						${getNumBackInUnits(closePositionMarket?.currentMarketPrice, 18, 2)}
					{:else}
						-
					{/if}
				</div>
			</div>
			<div class="flex justify-between items-end">
				<div class="text-xs sm:text-sm text-fx-grey-400">Avg. Entry Price</div>
				<div class="text-xs sm:text-sm text-fx-grey-300">
					${getNumBackInUnits($currentClosePosition.avgEntryPrice, 18, 2)}
				</div>
			</div>
			<div class="flex justify-between items-end">
				<div class="text-xs sm:text-sm text-fx-grey-400">Total Margin</div>
				<div class="text-xs sm:text-sm text-fx-grey-300">
					{#if closePositionMargin}
						${getNumBackInUnits(closePositionMargin, 18, 2)}
					{:else}
						-
					{/if}
				</div>
			</div>

			<div class="flex justify-between items-end">
				<div class="text-xs sm:text-sm text-fx-grey-400">Size</div>
				<div class="text-xs sm:text-sm text-fx-grey-300">
					{#if closePositionMarket}
						{addDollarSign(
							getNumBackInUnits(
								new BigNumber($currentClosePosition.size)
									.multipliedBy(closePositionMarket.currentMarketPrice)
									.div(1e18)
									.abs()
									.toString(),
								18,
								2
							)
						)}
					{:else}
						-
					{/if}
				</div>
			</div>

			{#if $closePositionOrderType == 'Market'}
				<ClosePositionMarket fees={expectedstandardOrderFee} />
				{#if $limitOrders.length > 0}
					{#each $limitOrders as limitOrder}
						{#if limitOrder.marketIdentifier == $currentClosePosition.marketIdentifier}
							<div class=" my-2 border squircle-xl rounded-xl border-fx-grey-600 p-2">
								<div class="text-fx-yellow text-sm font-semibold">
									You have limit orders in queue.
								</div>
								<div class="text-fx-600 text-xs">
									We've detected that you have open limit orders.
									<br />
									Do you want to keep them open?
								</div>
							</div>
						{/if}
					{/each}
				{/if}
			{:else}
				<ClosePositionLimit
					expectedLimitFee={totalLimitFee}
					avgEntryPrice={$currentClosePosition.avgEntryPrice}
				/>
			{/if}
			<!--
			 <EstimatedCloseOpRewards />
			 -->
			<div
				class="flex justify-between border border-fx-grey-600 bg-fx-gradient-8-50 rounded-lg p-2 my-2"
				class:border-fx-red={isAmountGreaterThanAvailable}
			>
				<input
					use:debounce={{ inputVal, func: getClosePositionPostTradeDetails, duration: 300 }}
					on:keyup={inputChanged}
					bind:value={inputVal}
					class="bg-transparent outline-none text-xs sm:text-sm"
					placeholder="Amount"
				/>

				<div class="text-fx-grey-100 text-xs sm:text-sm">
					<!-- remove s if the word starts with s, such as sETH to ETH -->
					{closePositionMarket?.asset.replace(/^s/, '')}
				</div>
			</div>
			<div class="flex justify-between">
				<button
					class="setAmountButton"
					on:click={() => {
						setRatioAmount(0.25);
					}}
				>
					25%
				</button>
				<button
					class="setAmountButton"
					on:click={() => {
						setRatioAmount(0.5);
					}}
				>
					50%
				</button>
				<button
					class="setAmountButton"
					on:click={() => {
						setRatioAmount(0.75);
					}}
				>
					75%
				</button>
				<button
					class="setAmountButton"
					on:click={() => {
						setRatioAmount(1);
					}}
				>
					100%
				</button>
			</div>
			<div>
				{#if closePositionMargin && new BigNumber(closePositionMargin).lt(new BigNumber(expectedstandardOrderFee))}
					<div class="text-xs sm:text-sm text-fx-yellow pt-3 font-semibold text-center">
						Margin below required to close position.
					</div>
				{/if}
			</div>
			{#if $walletLoaded && $walletConnected && $chain?.id != 10}
				<div class="my-4"><SwitchToOptimismButton /></div>
			{:else if $closingPosition}
				<div class="my-4">
					<ProcessingButton title="Closing Position" />
				</div>
			{:else if isAmountValid == false}
				<div class="my-4">
					<DisabledButton title="Close Position" />
				</div>
			{:else if !inputVal}
				<div class="my-4">
					<DisabledButton title="Enter Amount" />
				</div>
			{:else if $loadingClosePositionPostTradeDetails}
				<div class="my-4">
					<ProcessingButton title="Close Position" />
				</div>
			{:else if !$loadingClosePositionPostTradeDetails && $closePositionPostTradeDetails && invalidLimitOrderStatus.includes($closePositionPostTradeDetails.status)}
				<div class="my-4">
					<DisabledButton
						title={getTradeStatus(
							TradeStatus[$closePositionPostTradeDetails.status],
							'closePosition'
						)}
					/>
				</div>
			{:else if !$loadingClosePositionPostTradeDetails && $closePositionPostTradeDetails && closePositionMargin && new BigNumber($closePositionPostTradeDetails.fee)
					.plus(new BigNumber('2').multipliedBy(1e18))
					.minus(new BigNumber(closePositionMargin))
					.gt(0)}
				<div class="my-4">
					<button
						on:click={() => openAdjustMarginModal($currentClosePosition.marketIdentifier)}
						class="my-4 rounded-xl squircle-xl bg-fx-gradient-9 py-3 sm:py-4 text-fx-black font-semibold text-center cursor-pointer text-xs sm:text-sm w-full"
					>
						Adjust margin
					</button>
				</div>
			{:else if closePositionMargin && new BigNumber(closePositionMargin).lt(new BigNumber(expectedstandardOrderFee))}
				<div class="my-4">
					<button
						on:click={() => openAdjustMarginModal($currentClosePosition.marketIdentifier)}
						class="my-4 rounded-xl squircle-xl bg-fx-gradient-9 py-3 sm:py-4 text-fx-black font-semibold text-center cursor-pointer text-xs sm:text-sm w-full"
					>
						Adjust margin
					</button>
				</div>
			{:else if $closePositionOrderType == 'Limit' && $ClosePositionLimitStatus != LimitStatus.Ok}
				<div class="my-4">
					<DisabledButton title={getTradeStatus(LimitStatus[$ClosePositionLimitStatus])} />
				</div>
			{:else}
				<div class="my-4">
					<button
						class="rounded-xl squircle-xl bg-fx-gradient-9 py-3 sm:py-4 text-fx-black font-semibold text-center cursor-pointer text-xs sm:text-sm w-full"
						on:click={handleClosePosition}
						on:keydown={handleClosePosition}
					>
						Close Position
					</button>
				</div>
			{/if}
		</div>
		<div class="border-t border-t-fx-grey-800 w-full my-4" />
		<div class="flex justify-between items-center">
			<a rel="noreferrer" href="https://docs.trade.polynomial.fi/" target="_blank"
				><div class="flex gap-1 items-center">
					<div class="text-xs text-fx-pink">Docs</div>
					<img loading="lazy" src={'link-grey.png'} class="w-4 h-4" alt="help" />
				</div>
			</a>
			{#if closePositionMarket}
				<div class="text-xs text-fx-grey-400">
					Contract address:<span class="text-fx-yellow font-medium ml-1">
						{getShortAddress(MARKET_ADDRESES[closePositionMarket?.marketIdentifier])}
					</span>
				</div>
			{/if}
		</div>
	</div>
</GradientBorder>

<style>
	/* Chrome, Safari, Edge, Opera */
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	/* Firefox */
	.setAmountButton {
		@apply cursor-pointer w-12 text-center py-1 text-xs text-fx-grey-200 bg-fx-grey-700 rounded-md font-semibold;
	}

	.active-order-execution-type {
		@apply border border-fx-grey-500 text-fx-grey-200 font-semibold;
	}
</style>
